import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, LogOut } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ShippingBanner from "@/components/ShippingBanner";

export default function Header() {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const { count, setOpen } = useCart();
    const [mobile, setMobile] = useState(false);
    const navigate = useNavigate();

    const links = [
        { to: "/", label: t("nav.home"), key: "home" },
        { to: "/shop", label: t("nav.shop"), key: "shop" },
        { to: "/bulk-quote", label: t("nav.bulkQuote"), key: "bulk-quote" },
        { to: "/faq", label: t("nav.faq"), key: "faq" },
        { to: "/research", label: t("nav.research"), key: "research" },
        { to: "/about", label: t("nav.about"), key: "about" },
        { to: "/contact", label: t("nav.contact"), key: "contact" },
    ];

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <header
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/70 border-b border-neutral-200"
            data-testid="site-header"
        >
            <ShippingBanner />
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between gap-2">
                <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                    <div className="relative w-8 h-8">
                        <div className="absolute inset-0 rounded-lg border border-[#00aeef] group-hover:rotate-45 transition-transform duration-500" />
                        <div className="absolute inset-1.5 rounded-lg bg-[#00aeef]/15 nx-glow" />
                        <div className="absolute inset-[10px] rounded-full bg-[#00aeef]" />
                    </div>
                    <div className="font-display font-extrabold uppercase tracking-[0.18em] text-sm">
                        Nexus<span className="text-[#0077b6]">.</span>Peptides
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.key}
                            to={l.to}
                            data-testid={`nav-${l.key}`}
                            className={({ isActive }) =>
                                `px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] transition-colors ${
                                    isActive ? "text-[#0077b6]" : "text-neutral-600 hover:text-neutral-900"
                                }`
                            }
                            end={l.to === "/"}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <LanguageSwitcher />

                    <button
                        onClick={() => setOpen(true)}
                        className="relative p-2.5 border border-neutral-200 rounded-lg hover:border-[#0089c8] transition-colors group"
                        data-testid="header-cart-btn"
                        aria-label={t("header.openCart")}
                    >
                        <ShoppingCart className="w-4 h-4 text-neutral-900 group-hover:text-black" />
                        {count > 0 && (
                            <span
                                className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-[#0089c8] text-white text-[10px] font-bold flex items-center justify-center"
                                data-testid="cart-count"
                            >
                                {count}
                            </span>
                        )}
                    </button>

                    {user && user !== false ? (
                        <div className="hidden md:flex items-center gap-2">
                            {user.role === "admin" && (
                                <Link
                                    to="/admin"
                                    className="px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#0077b6] border border-[#00aeef]/40 hover:bg-[#00aeef]/[0.06] rounded-lg transition-colors"
                                    data-testid="admin-link"
                                >
                                    {t("header.admin")}
                                </Link>
                            )}
                            <Link
                                to="/account"
                                className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-700 hover:text-black flex items-center gap-2"
                                data-testid="account-link"
                            >
                                <User className="w-4 h-4" />
                                {user.name?.split(" ")[0] || t("header.account")}
                            </Link>
                            <button
                                onClick={handleLogout}
                                className="p-2.5 border border-neutral-200 rounded-lg hover:border-[#0089c8] transition-colors"
                                data-testid="logout-btn"
                                aria-label={t("header.logout")}
                            >
                                <LogOut className="w-4 h-4 text-neutral-900" />
                            </button>
                        </div>
                    ) : (
                        <Link
                            to="/login"
                            className="hidden md:inline-flex px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-700 hover:text-black border border-neutral-200 hover:border-[#0089c8] transition-colors rounded-lg"
                            data-testid="login-link"
                        >
                            {t("header.signIn")}
                        </Link>
                    )}

                    <button
                        className="xl:hidden p-2 sm:p-2.5 border border-neutral-200 rounded-lg"
                        onClick={() => setMobile(!mobile)}
                        data-testid="mobile-menu-btn"
                        aria-label={t("header.menu")}
                    >
                        {mobile ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            {mobile && (
                <div className="xl:hidden border-t border-neutral-200 bg-white/95">
                    <div className="px-6 py-4 flex flex-col gap-2">
                        {links.map((l) => (
                            <NavLink
                                key={l.key}
                                to={l.to}
                                onClick={() => setMobile(false)}
                                className={({ isActive }) =>
                                    `px-4 py-3 text-sm font-semibold uppercase tracking-[0.18em] ${
                                        isActive ? "text-[#0077b6]" : "text-neutral-600"
                                    }`
                                }
                                end={l.to === "/"}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        {user && user !== false ? (
                            <>
                                {user.role === "admin" && (
                                    <Link
                                        to="/admin"
                                        onClick={() => setMobile(false)}
                                        className="px-4 py-3 text-sm font-bold uppercase tracking-[0.18em] text-[#0077b6]"
                                    >
                                        {t("header.admin")}
                                    </Link>
                                )}
                                <Link
                                    to="/account"
                                    onClick={() => setMobile(false)}
                                    className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-neutral-600"
                                >
                                    {t("header.account")}
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-neutral-600 text-left"
                                >
                                    {t("header.logout")}
                                </button>
                            </>
                        ) : (
                            <Link
                                to="/login"
                                onClick={() => setMobile(false)}
                                className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#0077b6]"
                            >
                                {t("header.signIn")}
                            </Link>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
